/*
 * NutriProVita header behaviour.
 *
 * Ports the three pieces of App.tsx state that cannot be expressed in CSS:
 *   navLocked        (App.tsx:377) — hamburger pins the rail open on desktop
 *   mobileNavOpen    (App.tsx:390)
 *   mobileSearchOpen (App.tsx:391)
 *
 * The hover half of the rail (navHovered) is pure CSS — see .npv-rail:hover in
 * npv-theme.css. Deliberately not registered against Sense's <menu-drawer>:
 * MenuDrawer.bindEvents() attaches a close handler to every <button> inside
 * itself (global.js:433-439), which would fight the controls in here.
 */
const NPV_MOBILE = 767;

class NpvHeader extends HTMLElement {
  connectedCallback() {
    this.rail = this.querySelector('[data-npv-rail]');
    this.dim = this.querySelector('[data-npv-dim]');
    this.mobileNav = this.querySelector('[data-npv-mobile-nav]');
    this.search = this.querySelector('[data-npv-search]');
    this.navToggle = this.querySelector('[data-npv-nav-toggle]');
    this.searchToggle = this.querySelector('[data-npv-search-toggle]');

    this.navToggle?.addEventListener('click', () => this.toggleNav());
    this.searchToggle?.addEventListener('click', () => this.toggleSearch());
    this.dim?.addEventListener('click', () => this.closeAll());

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.closeAll();
    });

    // Any [data-npv-open-nav] control anywhere on the page (e.g. a section's
    // "View All" link) opens the rail / mobile nav instead of navigating.
    // Delegated on document so it also catches nodes added by section reloads.
    this.onOpenNavClick = this.onOpenNavClick.bind(this);
    document.addEventListener('click', this.onOpenNavClick);

    // A resize across the breakpoint leaves the other mode's state stranded.
    this.onResize = this.onResize.bind(this);
    window.addEventListener('resize', this.onResize);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('click', this.onOpenNavClick);
    this.lockScroll(false);
  }

  onOpenNavClick(event) {
    const trigger = event.target.closest('[data-npv-open-nav]');
    if (!trigger) return;
    event.preventDefault();
    this.openNav();
  }

  openNav() {
    if (this.isMobile) {
      if (this.mobileNav) this.setMobileNav(true);
    } else if (this.rail) {
      this.setRailLocked(true);
    }
  }

  get isMobile() {
    return window.innerWidth <= NPV_MOBILE;
  }

  toggleNav() {
    if (this.isMobile) {
      this.setMobileNav(this.mobileNav.hasAttribute('hidden'));
    } else {
      this.setRailLocked(!this.rail.classList.contains('is-locked'));
    }
  }

  setRailLocked(open) {
    this.rail.classList.toggle('is-locked', open);
    this.dim.classList.toggle('is-visible', open);
    this.navToggle.classList.toggle('is-active', open);
    this.navToggle.setAttribute('aria-expanded', String(open));
  }

  setMobileNav(open) {
    this.mobileNav.toggleAttribute('hidden', !open);
    this.navToggle.classList.toggle('is-active', open);
    this.navToggle.setAttribute('aria-expanded', String(open));
    this.lockScroll(open);
    if (open) this.setSearch(false);
  }

  toggleSearch() {
    this.setSearch(!this.search.classList.contains('is-open'));
  }

  setSearch(open) {
    this.search.classList.toggle('is-open', open);
    this.searchToggle.setAttribute('aria-expanded', String(open));
    if (open) {
      this.setMobileNav(false);
      this.search.querySelector('input[type="search"]')?.focus();
    }
  }

  lockScroll(on) {
    document.body.classList.toggle('npv-scroll-locked', on);
  }

  closeAll() {
    if (this.rail) this.setRailLocked(false);
    if (this.mobileNav) this.setMobileNav(false);
    if (this.search) this.setSearch(false);
  }

  onResize() {
    if (this.isMobile) {
      this.setRailLocked(false);
    } else {
      this.setMobileNav(false);
      this.setSearch(false);
    }
  }
}

if (!customElements.get('npv-header')) {
  customElements.define('npv-header', NpvHeader);
}
